import { UploadOutlined } from '@ant-design/icons';
import { Form, notification, Upload } from 'antd';
import { Rule, RuleObject } from 'antd/lib/form';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FormItem } from 'src/components/Form/FormItem';
import { Modal } from 'src/components/Modal/Modal';
import { TransactionsStore } from 'src/stores/TransactionsStore/TransactionsStore';
import { CssColors } from 'src/styles/CssVariables';
import { ConstraintViolationError } from 'src/utils/ErrorDataHelper';
import { FormHelper } from 'src/utils/FormHelper';
import { addModalRenderer, ModalRendererProps } from 'src/utils/ModalRenderer';
import styled from 'styled-components';

type UploadTransactionsDialogProps = ModalRendererProps;

const { Dragger } = Upload;

const UploadTransactionsDialogFn = observer(
    ({ onSuccess, ...props }: UploadTransactionsDialogProps) => {
        const { t } = useTranslation();
        const [form] = Form.useForm();
        const draggerRef = useRef(null);

        useEffect(() => {
            return () => {
                TransactionsStore.uploadLoader.reset();
            };
        }, []);

        const fields = FormHelper.getFieldDataItems(
            TransactionsStore.uploadLoader
                .errorData as ConstraintViolationError,
        );

        const onFinish = async (values: UploadFormValues) => {
            await TransactionsStore.upload(values.file.file);
            if (!TransactionsStore.uploadLoader.hasError) {
                notification.success({
                    message: t(
                        'App.Components.UploadTransactionsDialog.Success',
                    ),
                });
                onSuccess?.();
            } else {
                return Promise.reject();
            }
        };

        return (
            <StyledModal
                title={t('App.Components.UploadTransactionsDialog.Title')}
                headerExtra={t(
                    'App.Components.UploadTransactionsDialog.SubTitle',
                )}
                okText={t('App.Components.UploadTransactionsDialog.Upload')}
                onFinish={onFinish}
                isLoading={TransactionsStore.uploadLoader.isLoading}
                form={form}
                formProps={{ fields }}
                {...props}
            >
                <FormItem
                    name={formNames.getPath('file')}
                    rules={[validateEmptyFileList]}
                >
                    <StyledDragger
                        accept=".xls,.xlsx"
                        multiple={false}
                        ref={draggerRef}
                        beforeUpload={() => false}
                        maxCount={1}
                    >
                        <UploadOutlined style={{ width: 40, height: 40 }} />
                        <div className="transactions-upload__text">
                            {t(
                                'App.Component.UploadTransactionsDialog.UploadText',
                            )}
                        </div>
                        <div className="transactions-upload__accept">
                            {t(
                                'App.Component.UploadTransactionsDialog.UploadAccept',
                            )}
                        </div>
                    </StyledDragger>
                </FormItem>
            </StyledModal>
        );
    },
);

const validateEmptyFileList: Rule = {
    validator: (_rule: RuleObject, { fileList } = {}) => {
        if (!fileList?.length) {
            return Promise.reject(
                <Trans i18nKey="Form.Rules.Validator.Messages.Required" />,
            );
        }

        return Promise.resolve();
    },
};

type UploadFormValues = {
    file: {
        file: File;
        fileList: File[];
    };
};
const formNames = FormHelper.createNameProvider<UploadFormValues>();

export const UploadTransactionsDialog = addModalRenderer(
    UploadTransactionsDialogFn,
);

const StyledModal = styled(Modal)`
    width: 468px !important;
`;

const StyledDragger = styled(Dragger)`
    &:hover,
    &.ant-upload-drag-hover {
        border-color: ${CssColors.black} !important;
    }

    .ant-upload {
        padding: 32px !important;
    }

    .ant-upload-drag-container {
        svg {
            width: 40px;
            height: 40px;
            color: ${CssColors.gray};
        }
    }

    .transactions-upload__text {
        font-size: 14px;
        margin-top: 12px;
    }

    .transactions-upload__accept {
        margin-top: 4px;
        color: ${CssColors.gray};
        font-size: 12px;
    }
`;
